import type { AssetImage } from "gql/generated";
import { getMultilingualString } from "shared/utils";
import { getCloudinary } from "shared/cloudinary";
import { useCallback, useEffect, useRef, useState } from "react";
import { useVideoMuxStatusRefreshMutation } from "shared/hooks/useMuxAssetMutations";

const getFileName = (text?: string) => {
  return text?.match(/([^/]+)\.\w+$/)?.[0];
};

export const isMuxVideoAsset = (image: Partial<AssetImage> | null | undefined) =>
  Boolean(image?.mux?.playbackId || image?.mux?.assetId);

export const isMuxVideoPreparing = (
  image: Partial<AssetImage> | null | undefined,
) =>
  Boolean(image?.mux?.assetId) &&
  (image?.mux?.status === "preparing" ||
    (image?.mux?.status !== "ready" && !image?.mux?.playbackId));

type UseImageMediaProps = {
  image: Partial<AssetImage>;
  onAssetUpdated?: (asset: Partial<AssetImage>) => void;
};

export const useImageMedia = ({ image, onAssetUpdated }: UseImageMediaProps) => {
  const canDownload = !isMuxVideoAsset(image);
  const cloudinaryImage = getCloudinary(image._id).image(image._id);
  const imageUrl = cloudinaryImage.toURL();

  const caption = getMultilingualString(image.caption ?? {});

  const [isCaptionOpen, setIsCaptionOpen] = useState(false);
  const [refreshMuxStatus] = useVideoMuxStatusRefreshMutation();
  const pollingRef = useRef(false);

  useEffect(() => {
    if (!image._id || !isMuxVideoPreparing(image)) return;

    let cancelled = false;
    const poll = async () => {
      if (pollingRef.current || cancelled) return;
      pollingRef.current = true;
      try {
        const result = await refreshMuxStatus({
          variables: { _id: image._id! },
        });
        const updated = result.data?.videoMuxStatusRefresh;
        if (updated && !cancelled) {
          onAssetUpdated?.(updated);
        }
      } catch {
        // Keep showing processing UI; next interval will retry.
      } finally {
        pollingRef.current = false;
      }
    };

    void poll();
    const interval = window.setInterval(poll, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    image._id,
    image.mux?.status,
    image.mux?.playbackId,
    image.mux?.assetId,
    onAssetUpdated,
    refreshMuxStatus,
  ]);

  const handleDownload = useCallback(async () => {
    try {
      if (!canDownload || !imageUrl) return;

      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = getFileName(url) || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error", error);
    }
  }, [canDownload, imageUrl]);

  return {
    caption,
    isCaptionOpen,
    setIsCaptionOpen,
    handleDownload,
    canDownload,
  };
};
